import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FOUNDING_ACCOUNT_QUERY_KEY,
  FOUNDING_ENROLLMENT_QUERY_KEY,
  FOUNDING_REPORT_QUERY_KEY,
  OWNER_PROPERTIES_QUERY_KEY,
} from "./constants";
import {
  assignFoundingBulk,
  editFoundingRates,
  setFoundingPartner,
  unassignFoundingBulk,
} from "./founding-api";

/**
 * A bulk assign/unassign changes both the founder cockpit report AND the
 * per-host property panel, so both caches must be invalidated — otherwise the
 * panel keeps showing the pre-mutation founding status of the rows it just
 * assigned.
 */
function useInvalidateAfterBulk() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: FOUNDING_REPORT_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: OWNER_PROPERTIES_QUERY_KEY });
  };
}

export function useAssignFoundingBulk() {
  const invalidate = useInvalidateAfterBulk();
  return useMutation({ mutationFn: assignFoundingBulk, onSuccess: invalidate });
}

export function useUnassignFoundingBulk() {
  const invalidate = useInvalidateAfterBulk();
  return useMutation({
    mutationFn: unassignFoundingBulk,
    onSuccess: invalidate,
  });
}

/**
 * Convert / un-convert an account to a Founding Partner (ADR-0010 §9).
 * Invalidates the account-status cache so the badge + section refresh.
 */
export function useSetFoundingPartner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setFoundingPartner,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FOUNDING_ACCOUNT_QUERY_KEY }),
  });
}

/**
 * Override a property's founding rates (ADR-0010, ADMIN only). The change
 * takes effect at the next renewal. Invalidates the per-property enrollment
 * cache plus the report / owner-property caches so every table that shows the
 * property's rates refreshes.
 */
export function useEditFoundingRates() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editFoundingRates,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: FOUNDING_ENROLLMENT_QUERY_KEY,
      });
      void queryClient.invalidateQueries({
        queryKey: FOUNDING_REPORT_QUERY_KEY,
      });
      void queryClient.invalidateQueries({
        queryKey: OWNER_PROPERTIES_QUERY_KEY,
      });
    },
  });
}
